<template>
  <div class="sd-card p-6">
    <div class="flex items-center gap-2 mb-4">
      <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
      </svg>
      <h3 class="text-lg font-semibold tracking-tight text-secondary-800">
        {{ $t('sales_dashboard.insights') }}
      </h3>
    </div>

    <div v-if="insights.length === 0" class="text-center text-secondary-400 py-6">
      {{ $t('sales_dashboard.no_insights') }}
    </div>

    <div v-else class="sd-insights-grid">
      <div 
        v-for="(insight, index) in insights" 
        :key="index"
        :class="getInsightClasses(insight.type)"
        class="sd-insight"
      >
        <component 
          :is="getInsightIcon(insight.type)" 
          class="sd-insight__icon"
        />
        <div class="flex-1 min-w-0">
          <h4 class="sd-insight__title">{{ insight.title }}</h4>
          <p class="sd-insight__description">{{ insight.description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Icon components using render functions (Vue runtime-only compatible)
const WarningIcon = {
  functional: true,
  render(h, context) {
    return h('svg', {
      attrs: { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
      class: context.data.class,
      staticClass: context.data.staticClass
    }, [
      h('path', {
        attrs: {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
        }
      })
    ]);
  }
};

const TrendingUpIcon = {
  functional: true,
  render(h, context) {
    return h('svg', {
      attrs: { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
      class: context.data.class,
      staticClass: context.data.staticClass
    }, [
      h('path', {
        attrs: {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
        }
      })
    ]);
  }
};

const LightbulbIcon = {
  functional: true,
  render(h, context) {
    return h('svg', {
      attrs: { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
      class: context.data.class,
      staticClass: context.data.staticClass
    }, [
      h('path', {
        attrs: {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
        }
      })
    ]);
  }
};

const TargetIcon = {
  functional: true,
  render(h, context) {
    return h('svg', {
      attrs: { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
      class: context.data.class,
      staticClass: context.data.staticClass
    }, [
      h('path', {
        attrs: {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
        }
      })
    ]);
  }
};

const ChartIcon = {
  functional: true,
  render(h, context) {
    return h('svg', {
      attrs: { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
      class: context.data.class,
      staticClass: context.data.staticClass
    }, [
      h('path', {
        attrs: {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
        }
      })
    ]);
  }
};

export default {
  name: 'InsightsCard',
  components: {
    WarningIcon,
    TrendingUpIcon,
    LightbulbIcon,
    TargetIcon,
    ChartIcon
  },
  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    kpis: {
      type: Object,
      default: () => ({})
    },
    salespersons: {
      type: Array,
      default: () => []
    },
    productStats: {
      type: [Array, Object],
      default: () => ({ products: [] })
    }
  },
  computed: {
    // Normalize productStats to always get products array
    products() {
      if (this.productStats?.products && Array.isArray(this.productStats.products)) {
        return this.productStats.products;
      }
      if (Array.isArray(this.productStats)) {
        return this.productStats;
      }
      return [];
    },
    insights() {
      const generatedInsights = [];
      
      if (!this.contracts || this.contracts.length === 0) {
        return [{
          type: 'suggestion',
          title: this.$t('sales_dashboard.insight_get_started'),
          description: this.$t('sales_dashboard.insight_get_started_desc'),
          priority: 1
        }];
      }

      // Year-over-year analysis
      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      
      const currentYearContracts = this.contracts.filter(c => {
        const year = new Date(c.created_at || c.start_date).getFullYear();
        return year === currentYear;
      });
      
      const lastYearContracts = this.contracts.filter(c => {
        const year = new Date(c.created_at || c.start_date).getFullYear();
        return year === lastYear;
      });

      if (lastYearContracts.length > 0) {
        const currentRevenue = this.calculateTotalRevenue(currentYearContracts);
        const lastRevenue = this.calculateTotalRevenue(lastYearContracts);
        
        if (lastRevenue > 0) {
          const growthPercent = ((currentRevenue - lastRevenue) / lastRevenue) * 100;
          
          if (growthPercent > 10) {
            generatedInsights.push({
              type: 'trend',
              title: `+${growthPercent.toFixed(0)}% ${this.$t('sales_dashboard.growth')} ${currentYear}`,
              description: this.$t('sales_dashboard.insight_revenue_grew', { 
                from: this.formatCompact(lastRevenue), 
                to: this.formatCompact(currentRevenue) 
              }),
              priority: 1
            });
          } else if (growthPercent < -10) {
            generatedInsights.push({
              type: 'warning',
              title: `${growthPercent.toFixed(0)}% ${this.$t('sales_dashboard.decline')} ${currentYear}`,
              description: this.$t('sales_dashboard.insight_revenue_declined'),
              priority: 1
            });
          }
        }
        
        // Contract count growth
        if (lastYearContracts.length > 0) {
          const countGrowth = ((currentYearContracts.length - lastYearContracts.length) / lastYearContracts.length) * 100;
          
          if (countGrowth > 20) {
            generatedInsights.push({
              type: 'opportunity',
              title: `${countGrowth.toFixed(0)}% ${this.$t('sales_dashboard.more_deals')}`,
              description: this.$t('sales_dashboard.insight_deals_increased', {
                from: lastYearContracts.length,
                to: currentYearContracts.length
              }),
              priority: 2
            });
          }
        }
      }

      // Margin insights
      if (this.kpis.avgMarginPercent) {
        if (this.kpis.avgMarginPercent < 30) {
          generatedInsights.push({
            type: 'warning',
            title: this.$t('sales_dashboard.insight_low_margin'),
            description: this.$t('sales_dashboard.insight_low_margin_desc', {
              margin: this.kpis.avgMarginPercent.toFixed(0)
            }),
            priority: 1
          });
        } else if (this.kpis.avgMarginPercent > 45) {
          generatedInsights.push({
            type: 'goal',
            title: this.$t('sales_dashboard.insight_strong_margin'),
            description: this.$t('sales_dashboard.insight_strong_margin_desc', {
              margin: this.kpis.avgMarginPercent.toFixed(0)
            }),
            priority: 3
          });
        }
      }

      // Contract length insights
      if (this.kpis.avgContractLengthMonths) {
        if (this.kpis.avgContractLengthMonths < 36) {
          generatedInsights.push({
            type: 'suggestion',
            title: this.$t('sales_dashboard.insight_extend_contracts'),
            description: this.$t('sales_dashboard.insight_extend_contracts_desc', {
              months: this.kpis.avgContractLengthMonths.toFixed(0)
            }),
            priority: 2
          });
        } else if (this.kpis.avgContractLengthMonths >= 48) {
          generatedInsights.push({
            type: 'goal',
            title: this.$t('sales_dashboard.insight_strong_contract_length'),
            description: this.$t('sales_dashboard.insight_strong_contract_length_desc', {
              months: this.kpis.avgContractLengthMonths.toFixed(0)
            }),
            priority: 3
          });
        }
      }

      // Churn insights
      if (this.kpis.churnRate) {
        if (this.kpis.churnRate > 5) {
          generatedInsights.push({
            type: 'warning',
            title: this.$t('sales_dashboard.insight_high_churn'),
            description: this.$t('sales_dashboard.insight_high_churn_desc', {
              rate: this.kpis.churnRate.toFixed(1)
            }),
            priority: 1
          });
        } else if (this.kpis.churnRate < 2) {
          generatedInsights.push({
            type: 'goal',
            title: this.$t('sales_dashboard.insight_low_churn'),
            description: this.$t('sales_dashboard.insight_low_churn_desc', {
              rate: this.kpis.churnRate.toFixed(1)
            }),
            priority: 3
          });
        }
      }

      // Top salesperson insight
      if (this.salespersons && this.salespersons.length > 0) {
        const topSeller = [...this.salespersons].sort((a, b) => 
          (b.totalRevenue || 0) - (a.totalRevenue || 0)
        )[0];
        
        if (topSeller && topSeller.name) {
          generatedInsights.push({
            type: 'goal',
            title: this.$t('sales_dashboard.insight_top_seller'),
            description: this.$t('sales_dashboard.insight_top_seller_desc', {
              name: topSeller.name,
              revenue: this.formatCompact(topSeller.totalRevenue || 0)
            }),
            priority: 3
          });
        }
      }

      // Manufacturer margin vs sales share insights (TWIG, SRT, etc.)
      const manufacturerStats = this.calculateManufacturerStats();
      manufacturerStats.forEach(mfr => {
        // High margin but low sales share - undersold
        if (mfr.marginPercent > 45 && mfr.salesSharePercent < 30 && mfr.salesSharePercent > 5) {
          generatedInsights.push({
            type: 'opportunity',
            title: `${mfr.name} ${this.$t('sales_dashboard.insight_undersold')}`,
            description: this.$t('sales_dashboard.insight_undersold_desc', {
              name: mfr.name,
              margin: mfr.marginPercent.toFixed(0),
              share: mfr.salesSharePercent.toFixed(0)
            }),
            priority: 2
          });
        }
        // Low margin but high sales - margin pressure
        if (mfr.marginPercent < 30 && mfr.salesSharePercent > 40) {
          generatedInsights.push({
            type: 'warning',
            title: `${mfr.name} ${this.$t('sales_dashboard.insight_margin_pressure')}`,
            description: this.$t('sales_dashboard.insight_margin_pressure_desc', {
              name: mfr.name,
              margin: mfr.marginPercent.toFixed(0),
              share: mfr.salesSharePercent.toFixed(0)
            }),
            priority: 1
          });
        }
      });

      // Contract vs cash sale ratio
      const contractRatio = this.calculateContractRatio();
      if (contractRatio.contractPercent >= 80) {
        generatedInsights.push({
          type: 'goal',
          title: this.$t('sales_dashboard.insight_strong_contract_base'),
          description: this.$t('sales_dashboard.insight_strong_contract_base_desc', {
            percent: contractRatio.contractPercent.toFixed(0)
          }),
          priority: 3
        });
      } else if (contractRatio.contractPercent < 50 && contractRatio.total > 10) {
        generatedInsights.push({
          type: 'suggestion',
          title: this.$t('sales_dashboard.insight_increase_contracts'),
          description: this.$t('sales_dashboard.insight_increase_contracts_desc', {
            percent: contractRatio.contractPercent.toFixed(0)
          }),
          priority: 2
        });
      }

      // Best selling product insight
      const topProducts = this.getTopProducts(3);
      if (topProducts.length > 0) {
        const topProduct = topProducts[0];
        const top3Names = topProducts.map(p => `${p.name} (${p.quantity})`).join(', ');
        generatedInsights.push({
          type: 'trend',
          title: `${this.$t('sales_dashboard.insight_bestseller')}: ${topProduct.name}`,
          description: this.$t('sales_dashboard.insight_bestseller_desc', {
            quantity: topProduct.quantity,
            top3: top3Names
          }),
          priority: 3
        });
      }

      // Product category concentration warning
      const categoryConcentration = this.calculateCategoryConcentration();
      if (categoryConcentration.topCategoryPercent > 70 && categoryConcentration.categoryCount > 1) {
        generatedInsights.push({
          type: 'suggestion',
          title: this.$t('sales_dashboard.insight_diversify_products'),
          description: this.$t('sales_dashboard.insight_diversify_products_desc', {
            category: categoryConcentration.topCategory,
            percent: categoryConcentration.topCategoryPercent.toFixed(0)
          }),
          priority: 2
        });
      }

      // Average contract value trend
      if (this.kpis.avgContractValue && this.kpis.avgContractValue > 50000) {
        generatedInsights.push({
          type: 'goal',
          title: this.$t('sales_dashboard.insight_high_contract_value'),
          description: this.$t('sales_dashboard.insight_high_contract_value_desc', {
            value: this.formatCompact(this.kpis.avgContractValue)
          }),
          priority: 3
        });
      } else if (this.kpis.avgContractValue && this.kpis.avgContractValue < 15000) {
        generatedInsights.push({
          type: 'suggestion',
          title: this.$t('sales_dashboard.insight_upsell_opportunity'),
          description: this.$t('sales_dashboard.insight_upsell_opportunity_desc', {
            value: this.formatCompact(this.kpis.avgContractValue)
          }),
          priority: 2
        });
      }

      // Salesperson performance spread
      if (this.salespersons && this.salespersons.length >= 3) {
        const revenues = this.salespersons.map(s => s.totalRevenue || 0).filter(r => r > 0);
        if (revenues.length >= 3) {
          const maxRev = Math.max(...revenues);
          const minRev = Math.min(...revenues);
          const avgRev = revenues.reduce((a, b) => a + b, 0) / revenues.length;
          
          if (maxRev > avgRev * 3 && minRev < avgRev * 0.3) {
            generatedInsights.push({
              type: 'suggestion',
              title: this.$t('sales_dashboard.insight_performance_gap'),
              description: this.$t('sales_dashboard.insight_performance_gap_desc'),
              priority: 2
            });
          }
        }
      }

      // Sort by priority and return top 4
      return generatedInsights
        .sort((a, b) => a.priority - b.priority)
        .slice(0, 4);
    }
  },
  methods: {
    getInsightIcon(type) {
      const icons = {
        warning: 'WarningIcon',
        opportunity: 'TrendingUpIcon',
        suggestion: 'LightbulbIcon',
        goal: 'TargetIcon',
        trend: 'ChartIcon'
      };
      return icons[type] || 'LightbulbIcon';
    },
    
    getInsightClasses(type) {
      const classes = {
        warning: 'sd-insight--warning',
        opportunity: 'sd-insight--opportunity',
        suggestion: 'sd-insight--suggestion',
        goal: 'sd-insight--goal',
        trend: 'sd-insight--trend'
      };
      return classes[type] || 'sd-insight--suggestion';
    },
    
    calculateTotalRevenue(contracts) {
      return contracts.reduce((sum, c) => {
        const value = parseFloat(c.rental_month_cost || c.cash_sum || 0);
        return sum + value;
      }, 0);
    },
    
    formatCompact(value) {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M kr`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}k kr`;
      }
      return `${Math.round(value)} kr`;
    },
    
    calculateManufacturerStats() {
      if (!this.products || this.products.length === 0) return [];
      
      const mfrMap = {};
      let totalRevenue = 0;
      
      this.products.forEach(product => {
        const mfr = product.manufacturer || 'other';
        const revenue = (product.totalRevenue || product.total_revenue || 0);
        const cost = (product.totalCost || product.purchase_cost || 0);
        
        if (!mfrMap[mfr]) {
          mfrMap[mfr] = { revenue: 0, cost: 0, quantity: 0 };
        }
        mfrMap[mfr].revenue += revenue;
        mfrMap[mfr].cost += cost;
        mfrMap[mfr].quantity += (product.quantity || product.totalQuantity || 0);
        totalRevenue += revenue;
      });
      
      return Object.entries(mfrMap)
        .map(([name, stats]) => ({
          name: name.toUpperCase(),
          marginPercent: stats.revenue > 0 ? ((stats.revenue - stats.cost) / stats.revenue) * 100 : 0,
          salesSharePercent: totalRevenue > 0 ? (stats.revenue / totalRevenue) * 100 : 0,
          revenue: stats.revenue,
          quantity: stats.quantity
        }))
        .filter(m => m.revenue > 0)
        .sort((a, b) => b.revenue - a.revenue);
    },
    
    calculateContractRatio() {
      if (!this.contracts || this.contracts.length === 0) {
        return { contractPercent: 0, cashPercent: 0, total: 0 };
      }
      
      // category values: 'rental', 'financed', 'cash', 'cash_services'
      // Contracts/recurring: rental, financed, cash_services (has monthly component)
      // Cash/one-time: cash
      const contractCategories = ['rental', 'financed', 'cash_services'];
      const cashCategories = ['cash'];
      
      const contracts = this.contracts.filter(c => 
        contractCategories.includes(c.category) || contractCategories.includes(c.agreement_category)
      ).length;
      const cash = this.contracts.filter(c => 
        cashCategories.includes(c.category) || cashCategories.includes(c.agreement_category)
      ).length;
      const total = this.contracts.length;
      
      return {
        contractPercent: total > 0 ? (contracts / total) * 100 : 0,
        cashPercent: total > 0 ? (cash / total) * 100 : 0,
        total
      };
    },
    
    getTopProducts(count = 3) {
      if (!this.products || this.products.length === 0) return [];
      
      return [...this.products]
        .sort((a, b) => (b.quantity || b.totalQuantity || 0) - (a.quantity || a.totalQuantity || 0))
        .slice(0, count)
        .map(p => ({
          name: p.display_name || p.name || p.productName || 'Okänd',
          quantity: p.quantity || p.totalQuantity || 0
        }));
    },
    
    calculateCategoryConcentration() {
      if (!this.products || this.products.length === 0) {
        return { topCategory: '', topCategoryPercent: 0, categoryCount: 0 };
      }
      
      const categoryMap = {};
      let totalQuantity = 0;
      
      this.products.forEach(product => {
        const cat = product.category || 'other';
        const qty = product.quantity || product.totalQuantity || 0;
        categoryMap[cat] = (categoryMap[cat] || 0) + qty;
        totalQuantity += qty;
      });
      
      const categories = Object.entries(categoryMap)
        .map(([name, qty]) => ({ name, qty }))
        .sort((a, b) => b.qty - a.qty);
      
      const topCategory = categories[0] || { name: '', qty: 0 };
      
      return {
        topCategory: topCategory.name,
        topCategoryPercent: totalQuantity > 0 ? (topCategory.qty / totalQuantity) * 100 : 0,
        categoryCount: categories.length
      };
    }
  }
};
</script>
